import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { SortDirectionType } from '../../core/models/sort-direction';
import { DataResponse } from '../../core/models/data-response';
import { suppressGlobalErrorNavigation } from '../../core/interceptors/error/suppress-global-error';
import { ConfigService } from '../../core/services/config/config.service';
import { UtilityService } from '../../core/services/utility/utility.service';
import { PatientPortalProxyUserRequest } from '../models/patient-portal-proxy-user-request';
import { ProxyUserRequestDetail } from '../models/proxy-user-request-detail';
import { RequestDetailPatient } from '../models/request-detail';

export interface PatientPortalProxyUserRequestsRequest {
  createdAfterUtc: string | null;
  searchTerm: string | null;
  /** Exact relationship to filter by (e.g. "Parent"). Null returns all relationships. */
  relationshipToPatient: string | null;
  workflowStatusTypeId: number | null;
  assignedToPatientPortalAdminUserId: number | null;
  unassignedOnly: boolean;
  sortDirection: SortDirectionType;
  sortColumn: string;
  pageNumber: number;
  pageSize: number;
}

export interface PatientPortalProxyUserRequestsResult {
  requests: PatientPortalProxyUserRequest[];
  totalRecordCount: number;
}

/**
 * Body for `Bolt/Patients/ProxySearch` — the proxy counterpart to
 * `PatientSearchRequest`. Same Bolt patient records, but audited against a
 * proxy request id, so it carries no `patientPortalAdminUserId` and (unlike the
 * linking-request search) no phone-number criterion.
 */
export interface ProxyPatientSearchRequest {
  patientPortalProxyUserRequestId: number;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  lastFiveSocialSecurityNumber: string | null;
  medicalRecordNumber: string | null;
  sortDirection: SortDirectionType;
  sortColumn: string;
  pageNumber: number;
  pageSize: number;
}

/**
 * Body for `PUT /PatientPortalProxyUserRequests/{id}/Approve` — grants the proxy
 * access to one Bolt patient record.
 *
 * `version` is the request's optimistic-concurrency token. Unlike the linking
 * queue's numeric version this one is a GUID string, so it is passed through
 * verbatim from the detail payload.
 */
export interface ApproveProxyRequestBody {
  /** Bolt patient the proxy is being linked to — the record staff selected. */
  patientId: number;
  version: string;
}

/** Body for `PUT /PatientPortalProxyUserRequests/{id}/Deny`. */
export interface DenyProxyRequestBody {
  patientPortalProxyUserRequestDenialReasonTypeId: number;
  /** Optional staff justification; written to the audit log. */
  note: string | null;
  version: string;
}

@Injectable({
  providedIn: 'root',
})
export class PatientPortalProxyUserRequestsService {
  private utilityService = inject(UtilityService);
  private configService = inject(ConfigService);
  private config = this.configService.config;
  private readonly requestsEndpoint = `${this.config.adminDashboardApiUrl}/PatientPortalProxyUserRequests`;

  getRequests(
    request: PatientPortalProxyUserRequestsRequest,
  ): Observable<PatientPortalProxyUserRequestsResult> {
    const searchEndpoint = this.requestsEndpoint + '/Search';
    return this.utilityService
      .postRequest<
        DataResponse<PatientPortalProxyUserRequest[]>
      >(searchEndpoint, request, false, false, true)
      .pipe(
        map((dataResponse) => ({
          requests: dataResponse.result,
          totalRecordCount: dataResponse.totalRecordCount,
        })),
      );
  }

  /**
   * Load one caregiver/proxy request for the detail screen.
   *
   * Opts out of the global `/error` navigation: the detail screen renders its own
   * "Request not found" / "Unable to load request" panel, and a navigation would
   * destroy the component before that panel could show. Same reason for the three
   * calls below — every one of them has in-place failure UI. The queue `Search`
   * above deliberately keeps the global navigation, because it has none.
   */
  getRequestById(patientPortalProxyUserRequestId: number): Observable<ProxyUserRequestDetail> {
    return this.utilityService.getRequest<ProxyUserRequestDetail>(
      `${this.requestsEndpoint}/${patientPortalProxyUserRequestId}`,
      false,
      suppressGlobalErrorNavigation(),
    );
  }

  /**
   * Patient search for the proxy detail screen. Reads the same Bolt patient
   * records as `PatientPortalUserRequestsService.searchPatients`, but through
   * the ProxySearch route so the call is audited against the proxy request.
   */
  searchPatients(request: ProxyPatientSearchRequest): Observable<RequestDetailPatient[]> {
    return this.utilityService
      .postRequest<
        DataResponse<RequestDetailPatient[]>
      >(`${this.config.adminDashboardApiUrl}/Bolt/Patients/ProxySearch`, request, false, false, true, suppressGlobalErrorNavigation())
      .pipe(map((response) => response.result));
  }

  /** Grants the proxy access to the selected patient record. */
  approveRequest(
    patientPortalProxyUserRequestId: number,
    body: ApproveProxyRequestBody,
  ): Observable<unknown> {
    return this.utilityService.putRequest<unknown>(
      `${this.requestsEndpoint}/${patientPortalProxyUserRequestId}/Approve`,
      body,
      false,
      suppressGlobalErrorNavigation(),
    );
  }

  /** Closes the request as denied, with a coded reason for the audit log. */
  denyRequest(
    patientPortalProxyUserRequestId: number,
    body: DenyProxyRequestBody,
  ): Observable<unknown> {
    return this.utilityService.putRequest<unknown>(
      `${this.requestsEndpoint}/${patientPortalProxyUserRequestId}/Deny`,
      body,
      false,
      suppressGlobalErrorNavigation(),
    );
  }
}
